import { useState, useMemo } from 'react';
import { Clock, Users, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookingDrawer } from '../components/BookingDrawer';
import { 
  CLASSES, DISCIPLINES, LEVELS, COACHES,
  formatDate, formatShortDate 
} from '../data/mockData';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const Schedule = () => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filters, setFilters] = useState({
    discipline: 'all',
    level: 'all',
    coach: 'all',
    time: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Generate week dates
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        day: date.toLocaleDateString('es-MX', { weekday: 'short' }),
        dayNum: date.getDate(),
        isToday: i === 0,
      });
    }
    return dates;
  }, []);

  // Filter classes
  const filteredClasses = useMemo(() => {
    return CLASSES.filter((cls) => {
      if (cls.date !== selectedDate) return false;
      if (filters.discipline !== 'all' && cls.discipline.toLowerCase() !== filters.discipline) return false;
      if (filters.level !== 'all' && cls.level !== filters.level) return false;
      if (filters.coach !== 'all' && cls.coachId !== parseInt(filters.coach)) return false;
      if (filters.time !== 'all') {
        const hour = parseInt(cls.time.split(':')[0]);
        if (filters.time === 'morning' && hour >= 12) return false;
        if (filters.time === 'afternoon' && (hour < 12 || hour >= 18)) return false;
        if (filters.time === 'evening' && hour < 18) return false;
      }
      return true;
    }).sort((a, b) => a.time.localeCompare(b.time));
  }, [selectedDate, filters]);

  const handleBookClass = (classData) => {
    setSelectedClass(classData);
    setIsDrawerOpen(true);
  };

  const clearFilters = () => {
    setFilters({
      discipline: 'all',
      level: 'all',
      coach: 'all',
      time: 'all',
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== 'all');

  const getStatusBadge = (status, availableSpots, totalSpots) => {
    if (status === 'full') {
      return <span className="status-badge status-full">Llena</span>;
    }
    if (status === 'few') {
      return <span className="status-badge status-few">{availableSpots} lugares</span>;
    }
    return <span className="status-badge status-available">{availableSpots}/{totalSpots}</span>;
  };

  const getLevelBadge = (level) => {
    const labels = { beginner: 'Principiante', intermediate: 'Intermedio', advanced: 'Avanzado', all: 'Todos' };
    return <span className={`level-badge ${level}`}>{labels[level]}</span>;
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-8 bg-slate-50" data-testid="schedule-page">
      <div className="container-app">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-2">
            Horarios
          </h1>
          <p className="text-slate-500">Encuentra y reserva tu próxima clase</p>
        </div>

        {/* Week Selector */}
        <div className="mb-6">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2">
            {weekDates.map((day) => (
              <button
                key={day.date}
                onClick={() => setSelectedDate(day.date)}
                className={`flex flex-col items-center justify-center min-w-[60px] h-[72px] rounded-2xl transition-all ${
                  selectedDate === day.date
                    ? 'bg-[#16263B] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`date-btn-${day.date}`}
              >
                <span className="text-xs font-medium uppercase">{day.day}</span>
                <span className="text-xl font-bold">{day.dayNum}</span>
                {day.isToday && (
                  <span className="text-[10px] font-medium">Hoy</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                hasActiveFilters ? 'bg-[#16263B] text-white' : 'bg-slate-100 text-slate-600'
              }`}
              data-testid="filter-toggle"
            >
              <Filter className="w-4 h-4" />
              Filtros
              {hasActiveFilters && (
                <span className="w-5 h-5 bg-black text-white rounded-full text-xs flex items-center justify-center">
                  {Object.values(filters).filter(v => v !== 'all').length}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="clear-filters"
              >
                Limpiar
              </button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-2xl animate-fade-in">
              <Select
                value={filters.discipline}
                onValueChange={(value) => setFilters({ ...filters, discipline: value })}
              >
                <SelectTrigger className="bg-white" data-testid="filter-discipline">
                  <SelectValue placeholder="Disciplina" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las disciplinas</SelectItem>
                  {DISCIPLINES.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.level}
                onValueChange={(value) => setFilters({ ...filters, level: value })}
              >
                <SelectTrigger className="bg-white" data-testid="filter-level">
                  <SelectValue placeholder="Nivel" />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.coach}
                onValueChange={(value) => setFilters({ ...filters, coach: value })}
              >
                <SelectTrigger className="bg-white" data-testid="filter-coach">
                  <SelectValue placeholder="Coach" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los coaches</SelectItem>
                  {COACHES.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.time}
                onValueChange={(value) => setFilters({ ...filters, time: value })}
              >
                <SelectTrigger className="bg-white" data-testid="filter-time">
                  <SelectValue placeholder="Horario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todo el día</SelectItem>
                  <SelectItem value="morning">Mañana (6-12)</SelectItem>
                  <SelectItem value="afternoon">Tarde (12-18)</SelectItem>
                  <SelectItem value="evening">Noche (18+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Date Header */}
        <div className="mb-4">
          <h2 className="font-heading font-semibold text-lg text-slate-900 capitalize">
            {formatDate(selectedDate)}
          </h2>
          <p className="text-sm text-slate-500">
            {filteredClasses.length} {filteredClasses.length === 1 ? 'clase' : 'clases'} disponibles
          </p>
        </div>

        {/* Classes List */}
        <div className="space-y-3" data-testid="classes-list">
          {filteredClasses.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl">
              <p className="text-slate-500 mb-2">No hay clases con estos filtros</p>
              <button
                onClick={clearFilters}
                className="text-sm font-medium text-[#16263B] hover:underline"
              >
                Ver todas las clases
              </button>
            </div>
          ) : (
            filteredClasses.map((cls) => (
              <div
                key={cls.id}
                className="card-premium p-4 md:p-5 cursor-pointer group"
                onClick={() => handleBookClass(cls)}
                data-testid={`schedule-class-${cls.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Time */}
                  <div className="flex flex-col items-center justify-center w-16 h-16 bg-slate-50 rounded-xl flex-shrink-0 group-hover:bg-[#16263B]/20 transition-colors">
                    <span className="font-heading font-bold text-xl text-slate-900">
                      {cls.time.split(':')[0]}
                    </span>
                    <span className="text-xs text-slate-500">:{cls.time.split(':')[1]}</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-slate-900">{cls.name}</h3>
                      {getLevelBadge(cls.level)}
                    </div>
                    <p className="text-sm text-slate-500 mb-2">{cls.coach}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {cls.duration} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {cls.totalSpots} lugares
                      </span>
                    </div>
                  </div>

                  {/* Status & Action */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {getStatusBadge(cls.status, cls.availableSpots, cls.totalSpots)}
                    <Button
                      size="sm"
                      className={`${cls.status === 'full' ? 'btn-secondary' : 'btn-primary'} px-4 py-2 h-auto text-xs`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookClass(cls);
                      }}
                      data-testid={`book-btn-${cls.id}`}
                    >
                      {cls.status === 'full' ? 'Lista espera' : 'Reservar'}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Booking Drawer */}
      <BookingDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        classData={selectedClass}
      />
    </div>
  );
};
